import { nameColumn } from "../../../../layouts/Table/columns/nameColumn";
import dateColumn from "../../../../layouts/Table/columns/dateColumn";
import { useLanguage } from "../../../../stores/LanguageStore";
import { useCallback, useEffect, useMemo, useState } from "react";
import MainTable from "../../../../layouts/Table/MainTable";
import {
  deleteWorkflow,
  editWorkflow,
  getWorkflows,
} from "../../../../api/WorkflowAPI";
import {
  ActionIcon,
  CloseButton,
  Flex,
  Group,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconCopyPlus, IconEdit } from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import { booleanColumn } from "../../../../layouts/Table/columns/booleanColumn";
import ItemDeleter from "../../../../components/TableComponents/ItemDeleter";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import SelectionDeleter from "../../../../components/TableComponents/SelectionDeleter";
import numberColumn from "../../../../layouts/Table/columns/numberColumn";
import { sharingColumn } from "../../../../layouts/Table/columns/sharingColumn";
import { handleWorkflowGet } from "../CreateCampaign/flow/flowUtils";
import { useNavigate } from "react-router-dom";
import WorkflowPreviewHover from "./WorkflowPreviewHover";

const WorkflowsListing = ({ closeModal, nurturing }) => {
  const lang = useLanguage((s) => s.language);
  const apiRef = useGridApiRef();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const navigate = useNavigate();
  const handleWorkflowImport = useCallback(
    (workflow, isEdit) => {
      modals.openConfirmModal({
        title: (
          <Text>
            {lang.campaign.import.confirm.title} <b>{workflow?.name}</b>
          </Text>
        ),
        children: (
          <Text size="sm">
            {lang.campaign.import.confirm.text1} <br />
            <b>{lang.campaign.import.confirm.text2}</b>
          </Text>
        ),
        centered: true,
        labels: {
          confirm: isEdit
            ? lang.campaign.import.use
            : lang.campaign.import.duplicate,
          cancel: lang.global.cancel,
        },
        onConfirm: () => {
          closeModal();

          if (nurturing) {
            return handleWorkflowGet({ id: workflow?.id, isEdit: false });
          }
          if (isEdit) {
            navigate(`/campaign/create/workflow/${workflow?.id}`);
          } else handleWorkflowGet({ id: workflow?.id, isEdit: false });
        },
      });
    },
    [
      closeModal,
      lang.campaign.import.confirm.text1,
      lang.campaign.import.confirm.text2,
      lang.campaign.import.confirm.title,
      lang.campaign.import.duplicate,
      lang.campaign.import.use,
      lang.global.cancel,
      navigate,
      nurturing,
    ]
  );

  const handleRowClick = ({ row }) => {
    handleWorkflowImport(row, true);
  };

  const columns = useMemo(
    () => [
      numberColumn(),
      nameColumn({
        apiRef,
        disableLink: true,
        leftSection: (row) => {
          if (row?.image_url) {
            return <WorkflowPreviewHover row={row} />;
          }
          return null;
        },
      }),
      booleanColumn({
        field: "programmed",
        headerName: lang.workflows.columns.programmed.name,
        falseLabel: lang.workflows.columns.programmed.false,
        trueLabel: lang.workflows.columns.programmed.true,
        width: 130,
        noColor: { error: true },
      }),
      dateColumn({
        field: "updated_on",
        headerName: lang.workflows.columns.updated_on,
      }),
      dateColumn(),
      sharingColumn({
        apiRef,
        editFunction: () => editWorkflow,
        allowResellerCustomers: true,
      }),
    ],
    [
      apiRef,
      lang.workflows.columns.programmed.name,
      lang.workflows.columns.programmed.false,
      lang.workflows.columns.programmed.true,
      lang.workflows.columns.updated_on,
    ]
  );

  const toolbarActions = useCallback(
    (ids) => {
      return (
        <SelectionDeleter
          handleDelete={deleteWorkflow}
          apiRef={apiRef}
          setTableLoading={setLoading}
          ids={ids}
        />
      );
    },
    [apiRef]
  );

  const actionsCell = useCallback(
    ({ row, value }) => {
      return (
        <Group wrap="nowrap" gap={4}>
          <Tooltip label={lang.campaign.import.use}>
            <ActionIcon
              variant="subtle"
              onClick={() => handleWorkflowImport(row, true)}
            >
              <IconEdit size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={lang.campaign.import.duplicate}>
            <ActionIcon
              variant="subtle"
              onClick={() => handleWorkflowImport(row)}
            >
              <IconCopyPlus size={16} />
            </ActionIcon>
          </Tooltip>
          <ItemDeleter
            title_bold={`${row.name} #${row.id}`}
            handleDelete={deleteWorkflow}
            deletedMessage={lang.workflows.delete_success}
            id={row.id}
            disabled={!row?.delete_permission}
            apiRef={apiRef}
          />
        </Group>
      );
    },
    [
      apiRef,
      handleWorkflowImport,
      lang.campaign.import.duplicate,
      lang.campaign.import.use,
      lang.workflows.delete_success,
    ]
  );

  useEffect(() => {
    if (loading) {
      let mainFunction = getWorkflows;
      mainFunction()
        .then((response) => {
          setRows(response.data.workflows.sort((a, b) => b.id - a.id));
        })
        .finally(() => setLoading(false));
    }
  }, [loading]);

  return (
    <Stack h={"72vh"} p={"lg"} pb={0} bg={"var(--mantine-color-primary-2)"}>
      <Group justify="space-between">
        <Text size="xl" fw={600}>
          {lang.campaign.import.title}
        </Text>

        <CloseButton size={"lg"} onClick={closeModal} mt={-32} mr={-16} />
      </Group>

      <Flex flex={1}>
        <MainTable
          apiRef={apiRef}
          columns={columns}
          rows={rows.filter((x) => !x?.nurturing)}
          loading={loading}
          setLoading={setLoading}
          id={"workflows-table"}
          toolbarActions={toolbarActions}
          onRowClick={handleRowClick}
          actionsCell={actionsCell}
          hasFolder
          pageName={"workflows"}
          disableCheckboxes
          whiteButtons
          tableSx={{
            "[data-field='name']": {
              paddingLeft: 3,
            },
            "& .MuiDataGrid-columnHeaders": {
              border: "none !important",
            },
            "& .MuiDataGrid-cell": {
              border: "none",
            },
            "& .MuiDataGrid-main": {
              background: "var(--mantine-color-primary-2)",
              border: "none !important",
              borderRadius: 3,
            },
            "& .table-search": {
              width: "300px !important",
            },
            "& .MuiDataGrid-footerContainer": {
              border: "none !important",
            },
            "& .table-row": {
              cursor: "pointer",
              borderRadius: 3,
              marginBottom: 1,
              background: "#fff",
              border: "1px solid var(--mantine-color-primary-1)",
            },
            "& .MuiDataGrid-pinnedColumnHeaders": {
              background: "transparent",
              borderRight: "none !important",
            },
            "& .MuiDataGrid-pinnedColumns--right": {
              background: "var(--mantine-color-primary-2) !important",
              border: "none !important",
            },
            "& .MuiDataGrid-pinnedColumns--left": {
              background: "var(--mantine-color-primary-2) !important",
              border: "none !important",
            },
            "& .MuiDataGrid-pinnedColumns--right .table-row": {
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              background: "#fff",
              border: "1px solid   var(--mantine-color-primary-1)",
              borderLeft: "none !important",
            },
            "& .MuiDataGrid-pinnedColumns--left .table-row": {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              background: "#fff",
              border: "1px solid   var(--mantine-color-primary-1)",
              borderRight: "none !important",
            },
            "& .MuiDataGrid-virtualScrollerRenderZone .table-row": {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            },
          }}
        />
      </Flex>
    </Stack>
  );
};

export default WorkflowsListing;
